import React from "react";
import Icon from "@mdi/react";
import { mdiMapMarker, mdiSignal } from "@mdi/js";
import { Link } from "react-router-dom";
import Layout from "../layouts/Default";
import Avatar from "../components/avatar/Avatar";
import Text from "../components/typography/Text";
import Button from "../components/buttons/Button";
import Tags from "../components/tags/Tags";
import Links from "../components/links/Links";
import Project from "../parts/Designer/Project";
import Slideshow from "../components/slideshow/Slideshow";
import Loader from "../components/loaders/Loader";
import ProjectDetails from "../parts/Designer/ProjectDetails";
import useMedia from "../hooks/useMedia";
import AuthContext from "../contexts/auth";
import fetchApi from "../fetchApi";
import typesContext from "../contexts/types";
import EditBar from "../parts/EditBar";
import EmptySVG from "../images/illustrations/empty_state.svg";
import NotFound from "./404";
import NoticePost from "../components/notice/NoticePost";
import Notice from "../components/notice/Notice";

export default ({ location, match, history }) => {
  const media = useMedia();
  const { skills, experiences } = React.useContext(typesContext);
  const { user } = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(true);
  const [designer, setDesigner] = React.useState(
    location.state ? location.state.designer : null
  );
  const [loadingWorks, setLoadingWorks] = React.useState(true);
  const [works, setWorks] = React.useState([]);
  const [slideshowIndex, setSlideshowIndex] = React.useState(false);
  const [showDetails, toggleDetails] = React.useReducer(s => !s, false);
  const { slug } = match.params;
  const loggedUser = user ? user.slug === slug : false;

  const fetchDesigner = React.useMemo(
    () => async () => {
      try {
        setLoading(true);
        const [fetchedDesigner] = await fetchApi(`/users?slug=${slug}`);
        setDesigner(fetchedDesigner);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );
  const notice =
    location.state && history.action === "PUSH" ? location.state.notice : false;
  const fetchWorks = React.useMemo(
    () => async () => {
      try {
        const fetchedWorks = await fetchApi(`/work?author_name=${slug}`);
        setWorks(fetchedWorks);
        setLoadingWorks(false);
      } catch (error) {
        console.error(error);
      }
    },
    [slug]
  );

  React.useEffect(() => {
    fetchDesigner();
    fetchWorks();
  }, [fetchDesigner, fetchWorks]); // eslint-disable-line

  if (loading)
    return (
      <Layout title="Loading...">
        <div style={{ marginBottom: "60px" }} />
        <Loader />
      </Layout>
    );
  else if (!slug || !designer) return <NotFound />;

  const projects = works
    ? works.map(project => ({
        ...project,
        content: <ProjectDetails {...project} />,
      }))
    : [];

  const designerSkills =
    skills && designer && Array.isArray(designer.skill)
      ? designer.skill
          .map(skillId => {
            const skillData = skills.find(({ id }) => id === skillId);
            if (skillData) return skillData.name;
            else return null;
          })
          .filter(Boolean)
      : [];

  return (
    <Layout
      title={designer.name}
      topBar={
        loggedUser ? (
          <EditBar
            actionLabel="Add a project"
            actionLink="/designer/editwork"
          />
        ) : null
      }
    >
      <div className="pDesigner">
        <div className="pDesigner_profile">
          <div className="pDesigner_part -header">
            <div className="pDesigner_header">
              <div className="pDesigner_picture">
                <Avatar avatar={designer.avatar} />
              </div>
              <div className="pDesigner_name">
                <h4>{designer.name}</h4>
                <Text small>{designer.job}</Text>
              </div>
            </div>

            <div className="pDesigner_buttons">
              {media === "mobile" && (
                <Button outlined onClick={toggleDetails} rounded>
                  Show details
                </Button>
              )}
              {!loggedUser && user && user.role !== "designer" && (
                <Link
                  to={{ pathname: "/designer/workwith", state: { designer } }}
                >
                  <Button black icon="send" rounded>
                    Work with me
                  </Button>
                </Link>
              )}
            </div>
            {(media !== "mobile" || showDetails) && (
              <Text small>{designer.bio}</Text>
            )}
          </div>

          {(media !== "mobile" || showDetails) && (
            <div className="pDesigner_details">
              {designer.skill && (
                <div className="pDesigner_part">
                  <h5>Skills</h5>
                  <Tags tags={designerSkills} />
                </div>
              )}
              <div className="pDesigner_part">
                <h5>
                  <Icon path={mdiMapMarker} size="19px" />
                  {designer.city}, {designer.country}
                </h5>
                <h5>
                  <Icon path={mdiSignal} size="19px" />
                  {experiences[designer.level]}
                </h5>
              </div>
              <div className="pDesigner_part">
                <Links {...designer} />
              </div>
            </div>
          )}
        </div>

        {/* Display Works */}
        {works && works.length > 0 && (
          <div className="pDesigner_content">
            {notice && loggedUser && (
              <NoticePost notice={notice} context="designer" />
            )}
            <div className="pDesigner_portfolio">
              {works.map((project, index) => (
                <Project
                  key={index}
                  {...project}
                  loggedUser={loggedUser}
                  onClick={() => setSlideshowIndex(index)}
                  refresh={fetchWorks}
                />
              ))}
            </div>
          </div>
        )}

        {/* Wait for works to load */}
        {loadingWorks && (
          <div className="pDesigner_empty">
            <Loader />
          </div>
        )}

        {/* No works for designer */}
        {(!works || works.length === 0) && !loadingWorks && (
          <div className="pDesigner_empty">
            {works.length < 1 && loggedUser && (
              <Notice type="success" optionName="hidden_designer_notice" />
            )}
            <img src={EmptySVG} alt="Empty" />
            <h4>
              {loggedUser ? "Your portfolio is empty." : "Portfolio is empty"}
            </h4>
          </div>
        )}
      </div>
      {slideshowIndex !== false && (
        <Slideshow
          show={slideshowIndex !== false}
          hide={() => setSlideshowIndex(false)}
          slide={slideshowIndex}
          items={projects}
        />
      )}
    </Layout>
  );
};
